/**
 * useTheme.ts
 *
 * Reads the ThemePref from preferences, resolves it to an actual 'dark' |
 * 'light' value (resolving 'system' via matchMedia), and keeps
 * data-theme on <html> in sync.
 *
 * Also initialises data-animation-mode on first mount so the attribute
 * is present before any component renders.
 *
 * Returns { pref, resolved, setPref } for use by PreferencesSection or
 * any settings UI that needs to read/write the current theme.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  getThemePref,
  setThemePref,
  getAnimationMode,
  type ThemePref,
} from '../lib/preferences';

export type ResolvedTheme = 'dark' | 'light';

export interface UseThemeReturn {
  /** The stored preference — 'dark' | 'light' | 'system' */
  pref: ThemePref;
  /** The effective theme after resolving 'system' via OS preference */
  resolved: ResolvedTheme;
  /** Update the stored preference and immediately apply it */
  setPref: (pref: ThemePref) => void;
}

function resolveTheme(pref: ThemePref): ResolvedTheme {
  // SSR guard: matchMedia is browser-only. Return 'dark' (codebase default)
  // during server render; client-side useEffect re-resolves with actual pref.
  if (typeof window === 'undefined' || typeof window.matchMedia === 'undefined') {
    return 'dark';
  }
  if (pref === 'system') {
    return window.matchMedia('(prefers-color-scheme: light)').matches
      ? 'light'
      : 'dark';
  }
  return pref;
}

function applyTheme(resolved: ResolvedTheme): void {
  if (resolved === 'light') {
    document.documentElement.setAttribute('data-theme', 'light');
  } else {
    document.documentElement.removeAttribute('data-theme');
  }
}

export function useTheme(): UseThemeReturn {
  const [pref, setPrefState] = useState<ThemePref>(() => getThemePref());
  const [resolved, setResolved] = useState<ResolvedTheme>(() =>
    resolveTheme(getThemePref()),
  );

  // Apply theme and initialise data-animation-mode on first mount.
  useEffect(() => {
    applyTheme(resolved);
    // Initialise animation-mode attribute from stored preference so
    // CSS [data-animation-mode] selectors work immediately.
    document.documentElement.setAttribute(
      'data-animation-mode',
      getAnimationMode(),
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep data-theme in sync whenever resolved changes.
  useEffect(() => {
    applyTheme(resolved);
  }, [resolved]);

  // Listen for OS preference changes when pref === 'system'.
  useEffect(() => {
    if (pref !== 'system') return;

    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const handler = (e: MediaQueryListEvent) => {
      const next: ResolvedTheme = e.matches ? 'light' : 'dark';
      setResolved(next);
    };

    mq.addEventListener('change', handler);
    return () => {
      mq.removeEventListener('change', handler);
    };
  }, [pref]);

  const setPref = useCallback((next: ThemePref) => {
    setThemePref(next);
    setPrefState(next);
    setResolved(resolveTheme(next));
  }, []);

  return { pref, resolved, setPref };
}
